import { Globe, Image as ImageIcon } from "lucide-react";
import type { ComponentProps, Ref } from "react";

import { cn } from "@/lib/utils";

export type ImageThumbnailVariant = "missing" | "web";

export interface ImageThumbnailProps
    extends Pick<ComponentProps<"img">, "onLoad" | "onError"> {
    /** Image URL. When omitted, renders the placeholder for `variant`. */
    src?: string;
    alt?: string;
    /**
     * Placeholder style when there is no image.
     * - `missing` (default) — zinc + Image icon (e.g. catalog without photos)
     * - `web` — blue + Globe (e.g. web-sourced results)
     */
    variant?: ImageThumbnailVariant;
    className?: string;
    imageRef?: Ref<HTMLImageElement>;
}

/**
 * Reusable image thumbnail for prototype lists, carousels, and cards.
 * Shows a photo when `src` is set; otherwise a variant-specific placeholder.
 */
export function ImageThumbnail({
    src,
    alt = "",
    variant = "missing",
    className,
    imageRef,
    onLoad,
    onError,
}: ImageThumbnailProps) {
    if (src) {
        return (
            <img
                ref={imageRef}
                src={src}
                alt={alt}
                className={cn("bg-zinc-100 object-cover", className)}
                loading="lazy"
                decoding="async"
                onLoad={onLoad}
                onError={onError}
            />
        );
    }

    return <ImageThumbnailPlaceholder variant={variant} className={className} />;
}

function ImageThumbnailPlaceholder({
    variant,
    className,
}: {
    variant: ImageThumbnailVariant;
    className?: string;
}) {
    const isWeb = variant === "web";

    return (
        <div
            className={cn(
                "flex items-center justify-center",
                isWeb ? "bg-blue-100 text-blue-600" : "bg-zinc-100 text-zinc-400",
                className,
            )}
            aria-hidden
        >
            {isWeb ? (
                <Globe className="size-5" strokeWidth={2} />
            ) : (
                <ImageIcon className="size-5" strokeWidth={2} />
            )}
        </div>
    );
}
